import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Suspense } from "react";
import { listCrawlerLinks, type CrawlerLinkPage, type CrawlerLink } from "@/lib/crawler-links.functions";

/**
 * "Explore more on DrHint" strip — rendered as raw HTML `<a>` anchors,
 * SSR-safe, sitting in the exact DOM slot Googlebot preferentially
 * crawled on high-traffic pages. Admin-controlled via /admin-portal.
 *
 * First anchor is always the Site Directory, so every crawl also finds
 * the full HTML sitemap page.
 */
export function crawlerLinksQueryOptions(page: CrawlerLinkPage) {
  return queryOptions({
    queryKey: ["crawler-links", page],
    queryFn: () => listCrawlerLinks({ data: { page } }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function CrawlerLinksStrip({ page }: { page: CrawlerLinkPage }) {
  return (
    <Suspense fallback={<StripShell />}>
      <StripInner page={page} />
    </Suspense>
  );
}

function StripShell({ children }: { children?: React.ReactNode }) {
  return (
    <section
      className="mx-auto max-w-5xl px-4 mt-10 mb-6"
      aria-label="Explore more on DrHint"
      data-crawler-strip="1"
    >
      <div className="glass rounded-2xl p-5 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold mb-3">
          Explore more on DrHint
        </h2>
        <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm leading-relaxed">
          <li>
            <a
              href="/calculators"
              className="text-primary underline underline-offset-2 hover:opacity-80 font-medium"
            >
              Full DrHint Calculator Directory
            </a>
          </li>
          {children}
        </ul>
      </div>
    </section>
  );
}

function StripInner({ page }: { page: CrawlerLinkPage }) {
  const { data } = useSuspenseQuery(crawlerLinksQueryOptions(page));
  return (
    <StripShell>
      {data.map((row: CrawlerLink) => (
        <li key={row.id}>
          <a
            href={row.url}
            className="text-primary underline underline-offset-2 hover:opacity-80"
          >
            {row.label}
          </a>
        </li>
      ))}
    </StripShell>
  );
}